import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/api/axios';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import MyProperties from './MyProperties';

const tabData = [
    { value: 'visits', label: 'Visits' },
    { value: 'properties', label: 'Properties' },
];

const UserDashboard = () => {
    const [visits, setVisits] = useState([]);
    const [sales, setSales] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loadingVisits, setLoadingVisits] = useState(true);
    const [loadingSales, setLoadingSales] = useState(true);
    const [hasSales, setHasSales] = useState(false);
    const navigate = useNavigate();
    const { userInfo, loading } = useUser();

    // State for managing active tab
    const [activeTab, setActiveTab] = useState('visits');

    useEffect(() => {
        if (!loading && userInfo?.loginIdentifierDetails?.userId) {
            const fetchVisits = async () => {
                try {
                    const response = await axiosInstance.get('/users/property', {
                        params: {
                            userId: userInfo.loginIdentifierDetails.userId,
                        },
                    });

                    // Handle both paginated and non-paginated responses
                    const visitsArray = response?.items || (Array.isArray(response) ? response : []);

                    const currentTime = new Date();

                    const upcomingVisits = visitsArray.filter(
                        (visit) => new Date(visit.visitSchedule) > currentTime
                    );

                    const previousVisits = visitsArray.filter(
                        (visit) => new Date(visit.visitSchedule) <= currentTime
                    );

                    setVisits({ upcoming: upcomingVisits, previous: previousVisits });
                } catch (error) {
                    console.error('Error fetching visits:', error);
                } finally {
                    setLoadingVisits(false);
                }
            };

            fetchVisits();
        }
    }, [loading, userInfo]);

    useEffect(() => {
        if (!loading && userInfo?.loginIdentifierDetails?.userId) {
            const fetchSales = async () => {
                try {
                    const response = await axiosInstance.get('/customer/sales', {
                        params: {
                            userId: userInfo.loginIdentifierDetails.userId,
                            customerId: userInfo.loginIdentifierDetails.customerId,
                        },
                    });

                    // Handle both paginated and non-paginated responses
                    const salesArray = response?.items || (Array.isArray(response) ? response : []);

                    if (salesArray && salesArray.length > 0) {
                        setHasSales(true);
                        setSales(salesArray);

                        const formattedProperties = salesArray.map((sale) => ({
                            ...sale.propertyData,
                            saleDetails: {
                                customerSalesId: sale.customerSalesId,
                                basePrice: sale.basePrice,
                                saleDate: sale.saleDate,
                                paymentOption: sale.paymentOption,
                            },
                            installments: 'To be implemented',
                        }));
                        setProperties(formattedProperties);
                    }
                } catch (error) {
                    console.error('Error fetching sales:', error);
                } finally {
                    setLoadingSales(false);
                }
            };

            fetchSales();
        }
    }, [loading, userInfo]);

    const handleVisitClick = (property) => {
        navigate('/user-property', { state: { property: property } });
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="p-4 md:p-6 lg:p-8">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">User Dashboard</h2>

            {/* Tab Navigation */}
            <div className="px-4 py-6 flex justify-center"> {/* Added flex justify-center here */}
                <div className="flex justify-start border-b border-gray-300 w-fit">
                    {tabData.map((tab) => (
                        <div
                            key={tab.value}
                            onClick={() => setActiveTab(tab.value)}
                            className={`py-2 px-6 cursor-pointer text-center ${activeTab === tab.value
                                ? "text-gray-900 border-b-2 border-purple-500 font-semibold"
                                : "text-gray-600"
                                }`}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>

                {/* Content Based on Active Tab */}
                
            </div>
             <div className="p-2">
                    {activeTab === "visits" && (
                        <div className="mb-8">
                            <h3 className="text-xl font-semibold text-gray-700 mb-3">Upcoming Visits</h3>
                            {loadingVisits ? (
                                <div className="text-gray-500">Loading...</div>
                            ) : visits.upcoming.length === 0 ? (
                                <p className="text-gray-500">No upcoming visits.</p>
                            ) : (
                                <>
                                   
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {visits.upcoming.map((visit) => (
                                            <div
                                                key={visit.propertyConfig.propertyConfigId}
                                                className="bg-white shadow-md rounded-lg p-5"
                                            >
                                                
                                                <h2 className="text-lg font-semibold text-gray-900">{visit.propertyConfig.configName}</h2>
                                                <div className="mt-3 space-y-1 text-gray-700 text-sm">
                                                    <p><strong>🏢 Building:</strong> {visit?.building?.buildingName}</p>
                                                    <p><strong>📍 Project:</strong> {visit?.propertyConfig?.project?.projectName}</p>
                                                    <p><strong>👤 Agent:</strong> {visit?.agent?.ownerName}</p>
                                                    <p><strong>📅 Date/Time:</strong> {new Date(visit.visitSchedule).toLocaleString()}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}


                            <h3 className="text-xl font-semibold text-gray-700 mb-3 mt-6">Previous Visits</h3>
                            {loadingVisits ? (
                                <div className="text-gray-500">Loading...</div>
                            ) : visits.previous.length === 0 ? (
                                <p className="text-gray-500">No previous visits.</p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {visits.previous.map((visit) => (
                                        <div
                                            key={visit.propertyConfig.propertyConfigId}
                                            className="bg-white shadow-md rounded-lg p-5"
                                        >
                                            <h2 className="text-lg font-semibold text-gray-900">{visit.propertyConfig.configName}</h2>
                                            <div className="mt-3 space-y-1 text-gray-700 text-sm">
                                                <p><strong>🏢 Building:</strong> {visit?.building?.buildingName}</p>
                                                <p><strong>📍 Project:</strong> {visit?.propertyConfig?.project?.projectName}</p>
                                                <p><strong>👤 Agent:</strong> {visit?.agent?.ownerName}</p>
                                                <p><strong>📅 Date/Time:</strong> {new Date(visit.visitSchedule).toLocaleString()}</p>
                                                <span
                                                    className={`inline-block mt-2 px-2 py-1 text-xs font-medium rounded 
                                                ${visit.visited ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
                                                >
                                                    {visit.visited ? '✔ Visited' : '✖ Not Visited'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "properties" && (
                        <div className="mb-8">
                            <h3 className="text-xl font-semibold text-gray-700 mb-3">My Properties</h3>
                            {loadingSales ? (
                                <div className="text-gray-500">Loading...</div>
                            ) : (
                                <MyProperties properties={properties} handleVisitClick={handleVisitClick} />
                            )}
                        </div>
                    )}
                </div>
        </div>
    );
};

export default UserDashboard;